import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Shield, RefreshCw, Zap, Info } from 'lucide-react'

import { usePaymentFlow } from '@/lib/api/paymentFlows'
import { ErrorDisplay } from '@/components/ui/error-display'
import { parseTRPCError } from '@/lib/utils/trpc-error-utils'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { PAYMENT_FLOW_SUB_NAVIGATION_IDS, PaymentFlowSubNavigation } from '../components/sub-menu-navigation'
import { BackButton } from '@/components/ui/BackButton'
import { useOrgNavigate } from '@/hooks/use-app-navigate'

const Rebill3DSSkeleton = () => (
    <div>
        <div className="mb-4">
            <Skeleton className="h-9 w-full max-w-md" />
        </div>
        <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
                <Skeleton className="size-10" />
                <div className="space-y-2">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-4 w-96" />
                </div>
            </div>
        </div>
        <Skeleton className="h-[400px] w-full rounded-xl" />
    </div>
)

const Rebill3DSPageInner = () => {
    const { flowId } = useParams()
    const { data: paymentFlow, isLoading, error } = usePaymentFlow(flowId || '', !!flowId)

    if (!flowId) return null
    if (isLoading) return <Rebill3DSSkeleton />
    if (error) return <ErrorDisplay error={parseTRPCError(error)} />
    if (!paymentFlow) return <p>Payment flow not found</p>

    return <Rebill3DSContent paymentFlow={paymentFlow} />
}

const Rebill3DSContent = ({ paymentFlow }: { paymentFlow: any }) => {
    const { orgRoutes } = useOrgNavigate()
    const { t } = useTranslation(['paymentFlows', 'common'])

    return (
        <div>
            <PaymentFlowSubNavigation activeId={PAYMENT_FLOW_SUB_NAVIGATION_IDS.REBILL_3DS} />

            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <BackButton to={orgRoutes.PAYMENT_FLOW_DETAILS(paymentFlow.id)} />
                    <div>
                        <h1 className="text-2xl font-bold sm:text-3xl">
                            {t('paymentFlows:tabs.rebill3ds')}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {t('paymentFlows:rebill3ds.subtitle', { flowName: paymentFlow.name })}
                        </p>
                    </div>
                </div>
            </div>

            <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                <Info className="mt-0.5 size-4 shrink-0 text-blue-600 dark:text-blue-400" />
                <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-medium">{t('paymentFlows:rebill3ds.info_title')}</p>
                    <p className="mt-1 text-blue-700 dark:text-blue-400">
                        {t('paymentFlows:rebill3ds.info_description')}
                    </p>
                </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                {/* 3RI Mode */}
                <Card className="relative overflow-hidden">
                    <div className="absolute right-3 top-3">
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                            {t('paymentFlows:rebill3ds.coming_soon')}
                        </Badge>
                    </div>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="flex size-10 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                                <RefreshCw className="size-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                                <CardTitle className="text-lg">{t('paymentFlows:rebill3ds.mode_3ri.title')}</CardTitle>
                                <p className="text-xs text-muted-foreground">{t('paymentFlows:rebill3ds.mode_3ri.recommended')}</p>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            {t('paymentFlows:rebill3ds.mode_3ri.description')}
                        </p>

                        <div className="space-y-2 rounded-lg bg-muted/50 p-3">
                            <div className="flex items-center gap-2 text-xs">
                                <div className="size-1.5 rounded-full bg-emerald-500" />
                                <span>{t('paymentFlows:rebill3ds.mode_3ri.features.server_side')}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <div className="size-1.5 rounded-full bg-emerald-500" />
                                <span>{t('paymentFlows:rebill3ds.mode_3ri.features.fresh_cavv')}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <div className="size-1.5 rounded-full bg-emerald-500" />
                                <span>{t('paymentFlows:rebill3ds.mode_3ri.features.frictionless')}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <div className="size-1.5 rounded-full bg-emerald-500" />
                                <span>{t('paymentFlows:rebill3ds.mode_3ri.features.every_cycle')}</span>
                            </div>
                        </div>

                        <div className="rounded-lg border border-dashed border-muted-foreground/25 p-3 text-center">
                            <p className="text-xs text-muted-foreground">
                                {t('paymentFlows:rebill3ds.mode_3ri.config_placeholder')}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Dual Auth Mode */}
                <Card className="relative overflow-hidden">
                    <div className="absolute right-3 top-3">
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                            {t('paymentFlows:rebill3ds.coming_soon')}
                        </Badge>
                    </div>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <div className="flex size-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                                <Zap className="size-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                                <CardTitle className="text-lg">{t('paymentFlows:rebill3ds.mode_dual_auth.title')}</CardTitle>
                                <p className="text-xs text-muted-foreground">{t('paymentFlows:rebill3ds.mode_dual_auth.subtitle')}</p>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            {t('paymentFlows:rebill3ds.mode_dual_auth.description')}
                        </p>

                        <div className="space-y-2 rounded-lg bg-muted/50 p-3">
                            <div className="flex items-center gap-2 text-xs">
                                <div className="size-1.5 rounded-full bg-purple-500" />
                                <span>{t('paymentFlows:rebill3ds.mode_dual_auth.features.second_cit')}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <div className="size-1.5 rounded-full bg-purple-500" />
                                <span>{t('paymentFlows:rebill3ds.mode_dual_auth.features.separate_cavv')}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <div className="size-1.5 rounded-full bg-amber-500" />
                                <span>{t('paymentFlows:rebill3ds.mode_dual_auth.features.second_challenge')}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <div className="size-1.5 rounded-full bg-amber-500" />
                                <span>{t('paymentFlows:rebill3ds.mode_dual_auth.features.cycle_2_only')}</span>
                            </div>
                        </div>

                        <div className="rounded-lg border border-dashed border-muted-foreground/25 p-3 text-center">
                            <p className="text-xs text-muted-foreground">
                                {t('paymentFlows:rebill3ds.mode_dual_auth.config_placeholder')}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Current status */}
            <Card className="mt-6">
                <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                        <Shield className="size-5 text-muted-foreground" />
                        <div>
                            <p className="text-sm font-medium">{t('paymentFlows:rebill3ds.current_status.title')}</p>
                            <p className="text-xs text-muted-foreground">
                                {t('paymentFlows:rebill3ds.current_status.description')}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

export const Rebill3DSPage = withRequiredPermission(Rebill3DSPageInner, PERMISSIONS.PROCESSORS_MANAGE)
export default Rebill3DSPage
